import ClearIcon from '@mui/icons-material/Clear';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Skeleton,
  styled,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip as TooltipMui,
  Typography,
  useTheme
} from '@mui/material';
import BlockCard from 'components/Modal/variants/BlockCard';
import BlockedCard from 'components/Modal/variants/BlockedCard';
import dayjs from 'dayjs';
import { NfcCard } from 'graphql/generated/graphqlTypes';
import useCards from 'hooks/useCards/useCards';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import Cookies from 'js-cookie';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Icon from 'ui/atoms/Icon/Icon';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import { filterBySearchTerm } from 'utils/FilterBySearchTerm/FilterBySearchTerm.util';
import {
  getActionArray,
  getCardStatusesArray,
  getStatusPriority,
  isCardBlockedStatus,
  isCardExpiredStatus,
  isCardExpiresSoonStatus,
  isCardValidityExpiresIn,
  isRightsChangedStatus,
  isUserDeletedStatus,
  isUserExpiresSoonStatus
} from 'utils/NfcCard/nfcCard';
import { createHardwareTabs } from '../hardwareTabsConst';

const isBlockingCardsEnabled = process.env.REACT_APP_IS_BLOCKING_CARDS_ENABLED === 'true';

export const StyledPaper = styled(Paper)(() => ({
  backgroundColor: '#202020 !important',
  backgroundImage: 'none'
}));

export const createHeaders = (translation: Translation) => [
  { text: translation.card_id, width: '20%' },
  { text: translation.assigned_to, width: '20%' },
  { text: translation.card_valid_until, width: '20%' },
  { text: translation.status, width: '10%' },
  ...(isBlockingCardsEnabled ? [{ text: '', width: '10%' }] : [])
];

enum Order {
  asc = 'asc',
  desc = 'desc'
}

function descendingComparator<T>(aaa: T, bbb: T, orderBy: keyof T) {
  if (
    orderBy === 'assignment.visitorName' ||
    orderBy === 'assignment.expiryDate' ||
    orderBy === 'status' ||
    orderBy === 'action'
  ) {
    let itemToCompareAAA;
    let itemToCompareBBB;
    if (orderBy === 'assignment.visitorName') {
      itemToCompareAAA = (aaa as NfcCard).assignment?.visitorName || '';
      itemToCompareBBB = (bbb as NfcCard).assignment?.visitorName || '';
    }
    if (orderBy === 'assignment.expiryDate') {
      itemToCompareAAA = (aaa as NfcCard).assignment?.expiryDate || '';
      itemToCompareBBB = (bbb as NfcCard).assignment?.expiryDate || '';
    }
    if (orderBy === 'status') {
      itemToCompareAAA = getStatusPriority(aaa as NfcCard);
      itemToCompareBBB = getStatusPriority(bbb as NfcCard);
    }
    if (itemToCompareBBB! < itemToCompareAAA!) {
      return -1;
    }
    if (itemToCompareBBB! > itemToCompareAAA!) {
      return 1;
    }
  } else {
    if (bbb[orderBy as keyof T] < aaa[orderBy as keyof T]) {
      return -1;
    }
    if (bbb[orderBy as keyof T] > aaa[orderBy as keyof T]) {
      return 1;
    }
  }
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T): (aaa: T, bbb: T) => number {
  return order === 'desc'
    ? (aaa, bbb) => descendingComparator(aaa, bbb, orderBy)
    : (aaa, bbb) => -descendingComparator(aaa, bbb, orderBy);
}

interface FilterActionState {
  blockedCards: boolean;
  cardExpiresSoon: boolean;
  userDeleted: boolean;
  cardExpired: boolean;
  accessRightsChanged: boolean;
  userExpiresSoon: boolean;
}

type FilterCardValidityExpiresState = '' | '1day' | '3days' | '1week' | '1month';

type FilterStatusState = '' | 'assigned' | 'unassigned';

const filterActionDefaultState: FilterActionState = {
  blockedCards: false,
  cardExpiresSoon: false,
  accessRightsChanged: false,
  cardExpired: false,
  userDeleted: false,
  userExpiresSoon: false
};

const filterCardValidityExpiresDefaultState: FilterCardValidityExpiresState = '';

const filterStatusDefaultState: FilterStatusState = '';

const getPreferredHideBlockedCardsFromCache = () => {
  const cachedData = Cookies.get('hideBlockedCards');
  if (cachedData && cachedData === 'true') {
    return true;
  }
  return false;
};

const savePreferredHideBlockedCardsInCache = (value: boolean) => {
  Cookies.set('hideBlockedCards', value ? 'true' : 'false');
};

const Cards: React.FC = () => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const headers = createHeaders(translation);
  const hardwareTabs = createHardwareTabs(translation);
  const [filterStr, setFilterStr] = useState('');
  const [orderBy, setOrderBy] = useState<keyof NfcCard>('name');
  const [order, setOrder] = useState<Order>(Order.asc);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterAction, setFilterAction] = useState<FilterActionState>(filterActionDefaultState);
  const [filterCardValidityExpires, setFilterCardValidityExpires] = useState<FilterCardValidityExpiresState | null>(
    filterCardValidityExpiresDefaultState
  );
  const [filterStatus, setFilterStatus] = useState<FilterStatusState | null>(filterStatusDefaultState);
  const navigate = useNavigate();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { cards, loading, updateBlockNfcCard, updateBlockNfcCardMutationLoading } = useCards({
    handleFetchError
  });
  const [isBlockCardModalOpen, setIsBlockCardModalOpen] = useState(false);
  const [blockCardDescription, setBlockCardDescription] = useState<string>('');
  const [blockCardUuid, setBlockCardUuid] = useState<string | undefined>();
  const [isBlockedCardModalOpen, setIsBlockedCardModalOpen] = useState(false);
  const [blockedCardName, setBlockedCardName] = useState<string | undefined>();
  const [cardBlockedTime, setCardBlockedTime] = useState<dayjs.Dayjs | undefined>();
  const [hideBlockedCards, setHideBlockedCards] = useState<boolean>(getPreferredHideBlockedCardsFromCache());

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isFilterPopoverOpen = Boolean(anchorEl);
  const id = isFilterPopoverOpen ? 'simple-popover' : undefined;

  const handleCheckboxActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterAction({ ...filterAction, [event.target.name]: event.target.checked });
  };

  const handleRadioCardValidityExpiresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterCardValidityExpires(event.target.value as FilterCardValidityExpiresState);
  };

  const handleRadioStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterStatus(event.target.value as FilterStatusState);
  };

  const isAnyFilterEnabled =
    Object.values(filterAction).some(Boolean) || filterCardValidityExpires !== '' || filterStatus !== '';

  const handleResetAllFilters = () => {
    setFilterAction(filterActionDefaultState);
    setFilterStatus(filterStatusDefaultState);
    setFilterCardValidityExpires('');
    setFilterStr('');
  };

  const getCardValidUntilFormatTime = (cardExpired: string): string => dayjs(cardExpired).format('DD-MM-YYYY HH:mm');

  const getKeyByHeaderName = (header: string): string => {
    switch (header) {
      case translation.card_valid_until:
        return 'assignment.expiryDate';
      case translation.assigned_to:
        return 'assignment.visitorName';
      case translation.status:
        return 'status';
      case translation.card_id:
        return 'name';
      default:
      case translation.name:
        return 'name';
    }
  };

  const handleRequestSort = (property: keyof NfcCard) => {
    const isAsc = orderBy === property && order === Order.asc;
    setOrder(isAsc ? Order.desc : Order.asc);
    setOrderBy(property);
  };

  const getFilterData = useCallback(() => {
    if (cards) {
      let data = cards;
      if (isBlockingCardsEnabled && hideBlockedCards) {
        data = data.filter((card) => card.assignment && card.assignment.blocked === null);
      }
      data = filterBySearchTerm(data, filterStr, (card) => [
        card.assignment?.visitor?.name,
        card.assignment?.expiryDate ? getCardValidUntilFormatTime(card.assignment.expiryDate) : undefined,
        card.name,
        card.uuid,
        card.assignment?.visitorName,
        ...getCardStatusesArray(card, translation),
        ...getActionArray(card, translation)
      ]);

      if (Object.values(filterAction).some(Boolean))
        data = data.filter(
          (card) =>
            (filterAction.blockedCards && isCardBlockedStatus(card)) ||
            (filterAction.accessRightsChanged && isRightsChangedStatus(card)) ||
            (filterAction.cardExpiresSoon && isCardExpiresSoonStatus(card)) ||
            (filterAction.userDeleted && isUserDeletedStatus(card)) ||
            (filterAction.userExpiresSoon && isUserExpiresSoonStatus(card)) ||
            (filterAction.cardExpired && isCardExpiredStatus(card))
        );

      if (filterCardValidityExpires === '1day') {
        data = data.filter((card) => isCardValidityExpiresIn(card, 1, 'day'));
      }
      if (filterCardValidityExpires === '3days') {
        data = data.filter((card) => isCardValidityExpiresIn(card, 3, 'day'));
      }
      if (filterCardValidityExpires === '1week') {
        data = data.filter((card) => isCardValidityExpiresIn(card, 1, 'week'));
      }
      if (filterCardValidityExpires === '1month') {
        data = data.filter((card) => isCardValidityExpiresIn(card, 1, 'month'));
      }
      if (filterStatus === 'assigned') {
        data = data.filter((card) => !!card.assignment);
      }
      if (filterStatus === 'unassigned') {
        data = data.filter((card) => !card.assignment);
      }
      return data;
    }
    return [];
  }, [cards, filterStr, filterAction, filterCardValidityExpires, filterStatus, hideBlockedCards]);

  const manyStatusesChip = (cardStatuses: string[], statusToExclude: string) => {
    return (
      <Tooltip
        elementOnHover={
          <ComponentWrapper padding="4px" gap="4px" flexDirection="column">
            {cardStatuses
              .filter((status) => status !== statusToExclude)
              .map((status) => (
                <Typography noWrap>{status}</Typography>
              ))}
          </ComponentWrapper>
        }
        id="status-tooltip"
      >
        <ComponentWrapper cursor="pointer" alignItems="center">
          <Chip
            variant="filled"
            label={'+1'}
            size="small"
            sx={{
              '& .MuiChip-label': {
                fontWeight: 'medium'
              }
            }}
          ></Chip>
        </ComponentWrapper>
      </Tooltip>
    );
  };

  const sortedRows = useMemo(
    () => getFilterData()?.slice().sort(getComparator(order, orderBy)),
    [getFilterData, order, orderBy]
  );

  const getStatus = (card: NfcCard, translation: Translation) => {
    const cardStatuses = getCardStatusesArray(card, translation);
    const isManyStatuses = cardStatuses.length > 1;
    if (cardStatuses.includes(translation.blocked))
      return (
        <ComponentWrapper gap="8px">
          <Chip
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              color: 'rgba(255, 255, 255, 0.87)',
              fontWeight: 'bold'
            }}
            icon={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge
                  variant="dot"
                  sx={{
                    marginLeft: '8px',
                    marginRight: '4px',
                    alignSelf: 'center',
                    '& .MuiBadge-dot': {
                      backgroundColor: 'rgba(99, 102, 111, 1)',
                      width: '8px',
                      height: '8px'
                    }
                  }}
                />
              </Box>
            }
            variant="filled"
            label={translation.blocked}
            size="small"
            sx={{
              '& .MuiChip-label': {
                fontWeight: 'medium'
              }
            }}
          ></Chip>
          <TextSnippetOutlinedIcon
            style={{ color: 'rgba(255, 255, 255, 0.56)', cursor: 'pointer' }}
            onClick={() =>
              handleOpenBlockedCardModal(
                card.name,
                card.assignment?.blocked?.reason || '',
                dayjs(card.assignment?.blocked?.since)
              )
            }
            id="blocked-card-details-button"
          />
        </ComponentWrapper>
      );
    if (cardStatuses.includes(translation.card_expires_soon))
      return (
        <ComponentWrapper gap="8px">
          <TooltipMui id="action-tooltip" title={getAction(card, translation)} arrow placement="top">
            <ComponentWrapper cursor="pointer" alignItems="center">
              <Chip
                style={{
                  backgroundColor: 'rgba(237, 108, 2, 0.08)',
                  color: 'rgba(255, 226, 183, 1)',
                  fontWeight: 'bold'
                }}
                icon={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Badge
                      variant="dot"
                      sx={{
                        marginLeft: '8px',
                        marginRight: '4px',
                        alignSelf: 'center',
                        '& .MuiBadge-dot': {
                          backgroundColor: 'rgba(237, 108, 2, 1)',
                          width: '8px',
                          height: '8px'
                        }
                      }}
                    />
                  </Box>
                }
                variant="filled"
                label={translation.card_expires_soon}
                size="small"
                sx={{
                  '& .MuiChip-label': {
                    fontWeight: 'medium'
                  }
                }}
              ></Chip>
            </ComponentWrapper>
          </TooltipMui>

          {isManyStatuses && manyStatusesChip(cardStatuses, translation.card_expires_soon)}
        </ComponentWrapper>
      );

    if (cardStatuses.includes(translation.user_deleted))
      return (
        <ComponentWrapper gap="8px">
          <TooltipMui id="action-tooltip" title={getAction(card, translation)} arrow placement="top">
            <Chip
              icon={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge
                    variant="dot"
                    sx={{
                      marginLeft: '8px',
                      marginRight: '4px',
                      alignSelf: 'center',
                      '& .MuiBadge-dot': {
                        backgroundColor: 'rgba(244, 67, 54, 1)',
                        width: '8px',
                        height: '8px'
                      }
                    }}
                  />
                </Box>
              }
              style={{
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                color: 'rgba(244, 199, 199, 1)',
                fontWeight: 'bold'
              }}
              variant="filled"
              label={translation.user_deleted}
              size="small"
              sx={{
                '& .MuiChip-label': {
                  fontWeight: 'medium'
                }
              }}
            ></Chip>
          </TooltipMui>
          {isManyStatuses && manyStatusesChip(cardStatuses, translation.user_deleted)}
        </ComponentWrapper>
      );
    if (cardStatuses.includes(translation.card_expired))
      return (
        <ComponentWrapper gap="8px">
          <TooltipMui id="action-tooltip" title={getAction(card, translation)} arrow placement="top">
            <Chip
              style={{
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                color: 'rgba(244, 199, 199, 1)',
                fontWeight: 'bold'
              }}
              variant="filled"
              label={translation.card_expired}
              size="small"
              sx={{
                '& .MuiChip-label': {
                  fontWeight: 'medium'
                }
              }}
              icon={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge
                    variant="dot"
                    sx={{
                      marginLeft: '8px',
                      marginRight: '4px',
                      alignSelf: 'center',
                      '& .MuiBadge-dot': {
                        backgroundColor: 'rgba(244, 67, 54, 1)',
                        width: '8px',
                        height: '8px'
                      }
                    }}
                  />
                </Box>
              }
            ></Chip>
          </TooltipMui>
          {isManyStatuses && manyStatusesChip(cardStatuses, translation.card_expired)}
        </ComponentWrapper>
      );
    if (cardStatuses.includes(translation.rights_changed))
      return (
        <ComponentWrapper gap="8px">
          <TooltipMui id="action-tooltip" title={getAction(card, translation)} arrow placement="top">
            <Chip
              style={{
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                color: 'rgba(244, 199, 199, 1)',
                fontWeight: 'bold'
              }}
              variant="filled"
              label={translation.rights_changed}
              size="small"
              sx={{
                '& .MuiChip-label': {
                  fontWeight: 'medium'
                }
              }}
              icon={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge
                    variant="dot"
                    sx={{
                      marginLeft: '8px',
                      marginRight: '4px',
                      alignSelf: 'center',
                      '& .MuiBadge-dot': {
                        backgroundColor: 'rgba(244, 67, 54, 1)',
                        width: '8px',
                        height: '8px'
                      }
                    }}
                  />
                </Box>
              }
            ></Chip>
          </TooltipMui>
          {isManyStatuses && manyStatusesChip(cardStatuses, translation.rights_changed)}
        </ComponentWrapper>
      );
    if (cardStatuses.includes(translation.user_expires_soon))
      return (
        <ComponentWrapper gap="8px">
          <Chip
            style={{
              backgroundColor: 'rgba(237, 108, 2, 0.08)',
              color: 'rgba(255, 226, 183, 1)',
              fontWeight: 'bold'
            }}
            icon={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge
                  variant="dot"
                  sx={{
                    marginLeft: '8px',
                    marginRight: '4px',
                    alignSelf: 'center',
                    '& .MuiBadge-dot': {
                      backgroundColor: 'rgba(237, 108, 2, 1)',
                      width: '8px',
                      height: '8px'
                    }
                  }}
                />
              </Box>
            }
            variant="filled"
            label={translation.user_expires_soon}
            size="small"
            sx={{
              '& .MuiChip-label': {
                fontWeight: 'medium'
              }
            }}
          ></Chip>
          {isManyStatuses && manyStatusesChip(cardStatuses, translation.user_expires_soon)}
        </ComponentWrapper>
      );

    return <Typography variant="body2">{'-'}</Typography>;
  };

  const getAction = (card: NfcCard, translation: Translation) => {
    const actionArray = getActionArray(card, translation);
    if (actionArray.includes(translation.update_card))
      return <Typography variant="body2">{translation.update_card}</Typography>;
    if (actionArray.includes(translation.wipe_card_or_change_user))
      return <Typography variant="body2">{translation.wipe_card_or_change_user}</Typography>;

    return <Typography variant="body2">{'-'}</Typography>;
  };

  const handleOpenBlockCardModal = (cardUuid: string) => {
    setIsBlockCardModalOpen(true);
    setBlockCardUuid(cardUuid);
  };

  const handleCloseBlockCardModal = () => {
    setIsBlockCardModalOpen(false);
    setBlockCardUuid(undefined);
  };

  const handleBlockCard = () => {
    if (blockCardUuid && isBlockCardModalOpen) {
      try {
        updateBlockNfcCard(blockCardDescription, blockCardUuid);
      } finally {
        setIsBlockCardModalOpen(false);
      }
    }
  };

  const handleOpenBlockedCardModal = (cardName: string, cardDescription: string, blockedTime: dayjs.Dayjs) => {
    setIsBlockedCardModalOpen(true);
    setBlockedCardName(cardName);
    setBlockCardDescription(cardDescription);
    setCardBlockedTime(blockedTime);
  };

  const handleCloseBlockedCardModal = () => {
    setIsBlockedCardModalOpen(false);
    setBlockedCardName(undefined);
    setBlockCardDescription('');
    setCardBlockedTime(undefined);
  };

  const handleChangeHideBlockedCards = (value: boolean) => {
    setHideBlockedCards(value);
    savePreferredHideBlockedCardsInCache(value);
  };

  return (
    <StyledPaper elevation={2} style={{ height: '100%' }}>
      <ComponentWrapper gap="2rem" padding="2.5rem" flexDirection="column">
        <Typography variant="h4" color="textPrimary">
          TKH Security
        </Typography>
        <ComponentWrapper>
          <Tabs indicatorColor="primary" textColor="primary" value={1}>
            {hardwareTabs.map((tab) => (
              <Tab label={tab.text} onClick={() => navigate(tab.location!)} />
            ))}
          </Tabs>
        </ComponentWrapper>
        <Divider />
        <ComponentWrapper flexDirection="column" gap="1rem">
          <ComponentWrapper justifyContent="space-between">
            <ComponentWrapper width="100%" gap="8px">
              <TextField
                id="search-input"
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          fill: filterStr !== '' ? theme.palette.text.primary : theme.palette.text.secondary
                        }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment:
                    filterStr !== '' ? (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setFilterStr('')} aria-label="clear">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined
                }}
                placeholder="Search"
                onChange={(event) => setFilterStr(event.target.value)}
                value={filterStr}
                style={{ width: '240px', height: '40px' }}
              />
              <IconButton
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  border: `1px solid ${isAnyFilterEnabled ? 'rgba(204, 204, 0, 0.5)' : 'rgba(68, 68, 68, 1)'}`,
                  padding: '9px',
                  backgroundColor: `${isAnyFilterEnabled ? 'rgba(204, 204, 0, 0.12)' : 'transparent'}`
                }}
                onClick={handleClick}
              >
                <Badge variant="dot" color="primary" invisible={!isAnyFilterEnabled}>
                  <FilterAltOutlinedIcon style={{ color: 'rgba(255, 255, 255, 0.56)' }} id="filter-icon" />
                </Badge>
              </IconButton>
              <Popover
                id={id}
                open={isFilterPopoverOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
              >
                <ComponentWrapper flexDirection="column" margin="8px 16px">
                  <ComponentWrapper justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">{translation.status}</Typography>
                    <Button
                      disabled={!Object.values(filterAction).some(Boolean)}
                      onClick={() => setFilterAction(filterActionDefaultState)}
                      variant="text"
                      id="clear-status-button"
                    >
                      {translation.clear}
                    </Button>
                  </ComponentWrapper>
                  {isBlockingCardsEnabled && !hideBlockedCards && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filterAction.blockedCards}
                          onChange={handleCheckboxActionChange}
                          name="blockedCards"
                        />
                      }
                      label={translation.card_blocked}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterAction.cardExpiresSoon}
                        onChange={handleCheckboxActionChange}
                        name="cardExpiresSoon"
                      />
                    }
                    label={translation.card_expires_soon}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterAction.userDeleted}
                        onChange={handleCheckboxActionChange}
                        name="userDeleted"
                      />
                    }
                    label={translation.user_deleted}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterAction.cardExpired}
                        onChange={handleCheckboxActionChange}
                        name="cardExpired"
                      />
                    }
                    label={translation.card_expired}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterAction.accessRightsChanged}
                        onChange={handleCheckboxActionChange}
                        name="accessRightsChanged"
                      />
                    }
                    label={translation.access_rights_changed}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterAction.userExpiresSoon}
                        onChange={handleCheckboxActionChange}
                        name="userExpiresSoon"
                      />
                    }
                    label={translation.user_expires_soon}
                  />
                  <Divider />
                  <ComponentWrapper justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{translation.card_validity_expires_in}</Typography>
                    <Button
                      disabled={filterCardValidityExpires === ''}
                      onClick={() => setFilterCardValidityExpires('')}
                      variant="text"
                      id="clear-date-button"
                    >
                      {translation.clear}
                    </Button>
                  </ComponentWrapper>
                  <RadioGroup onChange={handleRadioCardValidityExpiresChange} value={filterCardValidityExpires}>
                    <FormControlLabel value="1day" control={<Radio />} label={translation['1day']} />
                    <FormControlLabel value="3days" control={<Radio />} label={translation['3days']} />
                    <FormControlLabel value="1week" control={<Radio />} label={translation['1week']} />
                    <FormControlLabel value="1month" control={<Radio />} label={translation['1month']} />
                  </RadioGroup>
                  <Divider />

                  <ComponentWrapper justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{translation.assigned}</Typography>
                    <Button
                      disabled={filterStatus === ''}
                      onClick={() => setFilterStatus('')}
                      variant="text"
                      id="clear-assigned-button"
                    >
                      {translation.clear}
                    </Button>
                  </ComponentWrapper>
                  <RadioGroup onChange={handleRadioStatusChange} value={filterStatus}>
                    <FormControlLabel value="assigned" control={<Radio />} label={translation.assigned} />
                    <FormControlLabel value="unassigned" control={<Radio />} label={translation.unassigned} />
                  </RadioGroup>
                </ComponentWrapper>
              </Popover>
              <Button
                size="large"
                variant="text"
                style={{
                  color: isAnyFilterEnabled ? 'rgba(255, 255, 255, 0.87)' : 'rgba(255, 255, 255, 0.38)',
                  padding: '0 8px'
                }}
                disabled={!isAnyFilterEnabled}
                onClick={handleResetAllFilters}
              >
                {translation.clear_filters}
              </Button>
            </ComponentWrapper>
            {isBlockingCardsEnabled && (
              <ComponentWrapper justifyContent="center" alignItems="center" gap="4px">
                <Switch checked={hideBlockedCards} onClick={() => handleChangeHideBlockedCards(!hideBlockedCards)} />
                <Typography variant="body1" noWrap>
                  {translation.hide_blocked_cards}
                </Typography>
              </ComponentWrapper>
            )}
          </ComponentWrapper>
        </ComponentWrapper>
        {cards && cards.length > 0 && !loading && (
          <TableContainer style={{ maxHeight: 'calc(100vh - 23rem)' }}>
            <Table stickyHeader style={{ borderCollapse: 'separate', borderSpacing: '0 0.25rem' }}>
              <TableHead>
                <TableRow>
                  {headers.map((header, index) => (
                    <TableCell style={{ border: 'none', backgroundColor: '#202020' }} width={header.width}>
                      {index < 3 && (
                        <TableSortLabel
                          active={orderBy === header.text}
                          direction={orderBy === getKeyByHeaderName(header.text) ? order : Order.asc}
                          onClick={() => handleRequestSort(getKeyByHeaderName(header.text) as keyof NfcCard)}
                          sx={
                            orderBy === getKeyByHeaderName(header.text)
                              ? {
                                  '& .MuiTableSortLabel-icon': {
                                    opacity: 1
                                  }
                                }
                              : undefined
                          }
                        >
                          {header.text}
                        </TableSortLabel>
                      )}
                      {index === 3 && (
                        <ComponentWrapper gap="4px" alignItems="center">
                          <TableSortLabel
                            active={orderBy === header.text}
                            direction={orderBy === getKeyByHeaderName(header.text) ? order : Order.asc}
                            onClick={() => handleRequestSort(getKeyByHeaderName(header.text) as keyof NfcCard)}
                            sx={
                              orderBy === getKeyByHeaderName(header.text)
                                ? {
                                    '& .MuiTableSortLabel-icon': {
                                      opacity: 1
                                    }
                                  }
                                : undefined
                            }
                          >
                            {header.text}
                          </TableSortLabel>
                          <TooltipMui
                            id="action-tooltip"
                            title={
                              <React.Fragment>
                                <span>
                                  {
                                    translation.actions_can_only_be_performed_by_scanning_the_card_with_an_admin_s_access_app
                                  }
                                </span>
                                <br />
                                <span>
                                  {translation.in_the_app_go_to_menu_my_organizations_your_organization_scan_card}
                                </span>
                              </React.Fragment>
                            }
                            arrow
                            placement="bottom"
                          >
                            <ComponentWrapper cursor="pointer">
                              <Icon name="Info" id="action-tooltip-icon" height={18} width={18} viewBox="0 0 24 24" />
                            </ComponentWrapper>
                          </TooltipMui>
                        </ComponentWrapper>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows?.map((card, index) => (
                  <TableRow
                    id={`row-${index}`}
                    sx={{
                      '&:hover': {
                        '& button': {
                          visibility: 'visible',
                          opacity: 1
                        }
                      },
                      '& button': {
                        visibility: 'hidden',
                        opacity: 0,
                        transition: 'opacity 0.2s ease-in-out'
                      }
                    }}
                  >
                    <TableCell
                      sortDirection={orderBy === card.name ? order : false}
                      width={headers[0].width}
                      style={{ border: 'none', margin: '6px 16px' }}
                    >
                      <Box>
                        <Typography variant="body2" id={`row-${index}-uuid`}>
                          {card.name}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell width={headers[1].width} style={{ border: 'none', margin: '6px 16px' }}>
                      <Box>
                        <Typography variant="body2" id={`row-${index}-assigned-to`}>
                          {card.assignment?.visitorName || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell width={headers[2].width} style={{ border: 'none', margin: '6px 16px' }}>
                      <Box>
                        <Typography variant="body2" id={`row-${index}-time`}>
                          {card.assignment?.expiryDate ? getCardValidUntilFormatTime(card.assignment.expiryDate) : '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell width={headers[3].width} style={{ border: 'none', margin: '6px 16px' }}>
                      <Box>{getStatus(card, translation)}</Box>
                    </TableCell>
                    {isBlockingCardsEnabled && (
                      <TableCell width={headers[4].width} style={{ border: 'none', margin: '6px 16px' }}>
                        <Box>
                          {card.assignment && card.assignment.blocked === null ? (
                            <Button
                              id={`row-${index}-block-card`}
                              onClick={() => handleOpenBlockCardModal(card.uuid)}
                              size="small"
                              color="error"
                              variant="text"
                              style={{ display: 'block', marginLeft: 'auto', whiteSpace: 'nowrap', height: '30.75px' }}
                            >
                              {translation.block}
                            </Button>
                          ) : (
                            <ComponentWrapper height="30.75px" />
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {loading && (
          <Table style={{ borderCollapse: 'separate', borderSpacing: '0 0.25rem' }}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell style={{ border: 'none', backgroundColor: '#202020' }}>{header.text}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }, (_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: headers.length }, (_, index) => (
                    <TableCell width={headers[index].width} style={{ border: 'none', padding: '0.375rem 1rem' }}>
                      <Skeleton width={headers[index].width} height={40} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && cards && cards.length > 0 && getFilterData()?.length === 0 && (
          <ComponentWrapper
            flexDirection="column"
            padding="1rem 0 8rem 0"
            gap="1.5rem"
            alignItems="center"
            justifyContent="center"
            height="30rem"
          >
            <Typography color="textPrimary" variant="h4">
              {translation.no_results}
            </Typography>
            <Typography variant="body1" color="secondary" align="center">
              {translation.this_may_be_the_answer_you_re_looking_for}
              <br />
              {translation.if_not_try_changing_your_search_term}
            </Typography>
          </ComponentWrapper>
        )}
        {!loading && cards && cards.length === 0 && (
          <ComponentWrapper
            flexDirection="column"
            padding="1rem 0 8rem 0"
            gap="1.5rem"
            alignItems="center"
            justifyContent="center"
            height="30rem"
          >
            <Typography color="textPrimary" variant="h4">
              {translation.no_access_cards}
            </Typography>
            <Typography variant="body1" color="secondary" align="center" width="550px">
              {
                translation.there_are_currently_no_access_cards_provisioned_to_your_organization_only_an_admin_using_the_atlas_access_app_can_provision_access_cards
              }
            </Typography>
          </ComponentWrapper>
        )}
        <Modal
          open={isBlockCardModalOpen}
          onClose={handleCloseBlockCardModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <BlockCard
            descriptionInputValue={blockCardDescription}
            setDescriptionInputValue={setBlockCardDescription}
            handleBlockCard={handleBlockCard}
            handleClose={handleCloseBlockCardModal}
          />
        </Modal>
        {isBlockedCardModalOpen && blockedCardName && cardBlockedTime && (
          <Modal
            open={isBlockedCardModalOpen}
            onClose={handleCloseBlockedCardModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <BlockedCard
              handleClose={handleCloseBlockedCardModal}
              cardName={blockedCardName}
              cardBlockedTime={cardBlockedTime}
              cardDescription={blockCardDescription}
            />
          </Modal>
        )}
      </ComponentWrapper>
    </StyledPaper>
  );
};

export default Cards;
